// Capture UTM parameters, referral code and first-visit metadata.
//
// Storage review / why localStorage (not sessionStorage or cookies):
// - Business rule: an adviser's referral credit must survive the visitor
//   closing the tab and coming back days later to register. sessionStorage is
//   wiped per tab; httpOnly cookies cannot be read by the form to attach the
//   code to the registration payload.
// - Only NON-sensitive marketing identifiers are stored (UTM tags, adviser
//   referral code, fbclid, landing URL, first-visit timestamp, random visitor
//   id). No names, emails, phone numbers or credentials are ever written.
// - Hardening: values are whitelisted + length-capped on every read (a
//   tampered store can never inject arbitrary keys/blobs into the payload),
//   and the record expires after ATTRIBUTION_TTL_DAYS so nothing lingers.
import { eventConfig } from "@/config/eventConfig";
import { logger } from "@/lib/logger";

const STORAGE_KEY = "cpf_attribution_v1";
const ATTRIBUTION_TTL_DAYS = 30;
const MAX_VALUE_LENGTH = 512;

const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
];

// Every key we are willing to read back from storage or forward to the API.
const ALLOWED_KEYS = new Set([
  ...UTM_KEYS,
  "referral_code",
  "fbclid",
  "landing_page_url",
  "first_visit_at",
]);

// Keep only whitelisted string values, trimmed to a sane length.
function sanitize(raw) {
  const clean = {};
  if (!raw || typeof raw !== "object") return clean;
  Object.keys(raw).forEach((k) => {
    const v = raw[k];
    if (ALLOWED_KEYS.has(k) && typeof v === "string" && v) clean[k] = v.slice(0, MAX_VALUE_LENGTH);
  });
  return clean;
}

function isStale(record) {
  const t = Date.parse(record.first_visit_at || "");
  if (Number.isNaN(t)) return false; // no timestamp: let it be refreshed below
  return Date.now() - t > ATTRIBUTION_TTL_DAYS * 24 * 60 * 60 * 1000;
}

function readStored() {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY));
    const clean = sanitize(parsed);
    return isStale(clean) ? {} : clean;
  } catch (e) {
    logger.debug("[attribution] could not read stored attribution", e);
    return {};
  }
}

export function captureAttribution() {
  if (typeof window === "undefined") return {};

  const stored = readStored();

  const params = new URLSearchParams(window.location.search);
  const fresh = {};

  UTM_KEYS.forEach((k) => {
    const v = params.get(k);
    if (v) fresh[k] = v;
  });

  // Referral code: support ?ref= and ?referral=
  const ref = params.get("ref") || params.get("referral");
  if (ref) fresh.referral_code = ref;

  // fbclid / gclid for ad platforms
  const fbclid = params.get("fbclid");
  if (fbclid) fresh.fbclid = fbclid;

  const merged = sanitize({
    ...stored,
    ...fresh,
    landing_page_url: stored.landing_page_url || window.location.href,
    first_visit_at: stored.first_visit_at || new Date().toISOString(),
  });

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
  } catch (e) {
    // Storage may be unavailable (private mode / quota); attribution still
    // works in-memory for this page load.
    logger.debug("[attribution] could not persist attribution", e);
  }

  return merged;
}

export function getReferralCode() {
  const data = captureAttribution();
  return data.referral_code || "";
}

// Stable per-browser visitor id (localStorage) to distinguish unique visitors
// from raw clicks in the link-traffic report.
const VISITOR_KEY = "cpf_visitor_id_v1";

export function getVisitorId() {
  if (typeof window === "undefined") return "";
  try {
    let id = localStorage.getItem(VISITOR_KEY);
    if (!id) {
      id =
        typeof crypto !== "undefined" && crypto.randomUUID
          ? crypto.randomUUID()
          : `v_${Date.now()}_${Math.random().toString(36).slice(2)}`;
      localStorage.setItem(VISITOR_KEY, id);
    }
    return id;
  } catch (e) {
    // localStorage blocked; fall back to an ephemeral id for this load.
    logger.debug("[attribution] visitor id storage unavailable", e);
    return `v_${Date.now()}_${Math.random().toString(36).slice(2)}`;
  }
}

// Map a referral code to a friendly adviser display name.
// Adviser list is centralised in eventConfig; legacy aliases kept for compat.
const LEGACY_ALIASES = {
  nicholas: "Nicholas Lee",
};

function buildAdviserMap() {
  const map = { ...LEGACY_ALIASES };
  (eventConfig.advisers || []).forEach((a) => {
    if (a && a.code) map[String(a.code).toLowerCase().trim()] = a.name;
  });
  return map;
}

export function getAdviserName(code) {
  if (!code) return "";
  const key = String(code).toLowerCase().trim();
  return buildAdviserMap()[key] || "";
}
